import { AgentSkill } from '../models/agent-config.model';

/**
 * Serialise an agent's bound skills for the `skills` structured input, as a JSON string so it
 * templates into a prompt as one `{{skills}}` value — the same shape `groundingConfig` is sent in.
 *
 * `command` leads each entry: it is the token a rep types as `/<command>`, so an agent seeing a
 * slash command in the message can map it to a skill exactly instead of matching prose against
 * names. `keywords` is split into a list rather than passed as the authored comma string, since
 * JSON can carry the structure the Dataverse column can't.
 *
 * The record id is omitted (it carries no meaning for the model) and so is `indskr_body`, which
 * the grounding MCP resolves server-side from `agentConfigId`. Undefined fields are dropped by
 * JSON.stringify, so a sparse skill stays sparse on the wire.
 *
 * Returns undefined when no skills are bound, so the input is omitted rather than sent empty.
 */
export function renderSkillsJson(skills: AgentSkill[] | undefined): string | undefined {
  if (!skills?.length) return undefined;
  return JSON.stringify(
    skills.map(s => ({
      command: s.command,
      name: s.name,
      description: s.description,
      keywords: splitKeywords(s.keywords),
    })),
  );
}

/** Authored keywords are one comma-separated string; carry them as a list, or omit when empty. */
function splitKeywords(keywords: string | undefined): string[] | undefined {
  const parts = (keywords ?? '')
    .split(',')
    .map(k => k.trim())
    .filter(Boolean);
  return parts.length ? parts : undefined;
}
